import PhoneInputComponent, { isValidPhoneNumber } from 'react-phone-number-input/min'
import flags from 'react-phone-number-input/flags'
import { useEffect, useState } from 'react'
import type { CountryCode } from 'libphonenumber-js'
import 'react-phone-number-input/style.css'

// Bug fix for react-phone-number-input library duplicating country code
const countryCodeDuplicatedPattern = /^\+(\d{1,4})(\d*)(\1)$/
const zeroPattern = /^\+(\d{1,4})\1$/

interface PhoneInputProps {
  value: string | undefined
  onChange: (value: string | undefined) => void
  defaultCountry?: CountryCode
  error?: string | null
  onValidChange?: (isValid: boolean) => void
}

export function PhoneInput({
  value,
  onChange,
  defaultCountry = 'AE',
  error: externalError,
  onValidChange,
}: PhoneInputProps) {
  const [error, setError] = useState(false)

  function fixCountryCode(phoneNumber: string | undefined) {
    if (!phoneNumber) return phoneNumber
    const zeroMatch = phoneNumber.match(zeroPattern)
    if (zeroMatch) {
      const [, countryCode] = zeroMatch
      if (countryCode.length > 1) {
        return countryCode.includes('+') ? countryCode : `+${countryCode}`
      }
    }
    const match = phoneNumber?.match(countryCodeDuplicatedPattern)
    if (match) {
      const [, countryCode, insertedDigits] = match
      if (insertedDigits) {
        return `+${countryCode}${insertedDigits}`
      }
    }
    return phoneNumber
  }

  useEffect(() => {
    if (!value) {
      onValidChange?.(false)
      return
    }
    const isValid = isValidPhoneNumber(value)
    onValidChange?.(isValid)
    if (!isValid && value?.length > 12) {
      setError(true)
    }
  }, [value, onValidChange])

  function handleChange(newValue: string | undefined) {
    if (newValue === value) return
    if (!value && newValue) {
      newValue = fixCountryCode(newValue)
    }
    onChange(newValue)
    if (error) setError(false)
  }

  function handleBlur() {
    if (!value) return
    const isValid = isValidPhoneNumber(value)
    if (!isValid && value.length > 6) {
      setError(true)
    }
  }

  return (
    <div className="w-full flex-col">
      <PhoneInputComponent
        placeholder="Enter phone number"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        defaultCountry={defaultCountry}
        international
        countryCallingCodeEditable={false}
        name="phone"
        smartCaret={false}
        flags={flags}
      />
      <div className={`${error || externalError ? 'h-8' : 'h-4'}`}>
        {error && (
          <p className="mt-1 text-xs text-destructive">Please enter a valid phone number</p>
        )}
        {externalError && !error && (
          <p className="mt-1 text-xs text-destructive">{externalError}</p>
        )}
      </div>
    </div>
  )
}
